import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Icon, Text } from '../../../../components';
import { useFeatureGate } from '../../../../hooks/useFeatureGate';
import { LogoMarkGlyph } from '../../../casino-settings/components/Glyphs/Glyphs';
import { areaTitle } from '../../area-copy';
import { WEBSITE_TABS, type WebsiteTab, type WebsiteTabId } from '../../tabs';
import { TabsBar, MainTab } from './WebsiteTabs.styles';

/** The row's own icon where it differs from the hub's: Website Creatives keeps the globe it always had here (the hub
 *  card and the area header use `image`, see tabs.ts). */
const ROW_ICON: Partial<Record<WebsiteTabId, WebsiteTab['icon']>> = { creatives: 'globe' };

/**
 * The Website section's tab row: Website Creatives · Logos · Art Style · Game Lists · Blog. Shown at the top of each
 * tab's page while the `website-hub` flag is off (with it on, the area header replaces it); the active tab is derived
 * from the URL and clicking a tab navigates to its route. A gated tab is dropped while its flag is off. Website
 * Creatives, Art Style and Game Lists ship generally available, their flags kept as rollback switches; Logos and Blog
 * are flagless.
 */
export const WebsiteTabs = (): React.ReactElement => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const gate = useFeatureGate();
  const tabs = WEBSITE_TABS.filter((tab) => gate.isEnabled(tab.flag));

  const onClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    const tab = tabs.find((candidate) => candidate.id === event.currentTarget.dataset.id);
    if (tab && !pathname.startsWith(tab.path)) navigate(tab.path);
  };

  return (
    <TabsBar role="tablist" aria-label={t('Website')}>
      {tabs.map((tab) => {
        const active = pathname === tab.path || pathname.startsWith(`${tab.path}/`);
        const icon = ROW_ICON[tab.id] ?? tab.icon;
        return (
          <MainTab
            key={tab.id}
            type="button"
            role="tab"
            data-id={tab.id}
            data-icon={icon}
            $active={active}
            aria-selected={active}
            onClick={onClick}
          >
            {icon === 'logo-mark' ? <LogoMarkGlyph size={16} /> : <Icon name={icon} size={16} />}
            <Text text={areaTitle(t, tab.id)} tag="span" raw />
          </MainTab>
        );
      })}
    </TabsBar>
  );
};

export default WebsiteTabs;
